import React, { useMemo } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { ThumbDown as ThumbDownIcon, ThumbUp as ThumbUpIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { filter, find, get, map } from 'lodash';

import { useCurrentLanguage } from '@lib/hooks';
import { PatientFormTemplateItemAnswer } from '@lib/resources/types';
import { getTranslation } from '@lib/services/patientFormsService';

import { Button, TypographyI18n } from 'components/v2';

import { ChatContextProps, useChatContext } from '../../contexts/ChatContext';

import ChatMessage, { ChatMessageProps } from './ChatMessage';
import { TMessageWrapper } from './MessageWrapper';

const styles = (theme: Theme) =>
  createStyles({
    choicesWrapper: {
      marginTop: theme.spacing.unit * 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      '&$yesNoType': {
        flexDirection: 'row',
        gap: `${theme.spacing.unit}px`,
      },
    },
    button: {
      marginBottom: theme.spacing.unit,
      display: 'grid',
      backgroundColor: theme.palette.common.white,
      '&:hover': {
        backgroundColor: fade(theme.palette.common.white, 0.8),
      },
      '&$yesNoType': {
        display: 'flex',
        marginBottom: 0,
        minWidth: 'fit-content',
      },
    },
    yesNoType: {},
  });

export interface MessageMultipleChoiceProps extends Omit<ChatMessageProps, 'classes'>, WithStyles<typeof styles> {
  message: TMessageWrapper;
  onSubmit: ChatContextProps['pushAnswer'];
}

const MessageMultipleChoice: React.FC<MessageMultipleChoiceProps> = ({ classes, message, onSubmit, ...rest }) => {
  const { flowTemplates, currentFlowTemplate } = useChatContext();
  const type = get(message.metadata.item, 'type');
  const language = useCurrentLanguage();
  const isTypeYesNo = type === 'yes_no';
  const allAnswers: PatientFormTemplateItemAnswer[] = get(message.metadata.item, 'properties.answers');

  const answers = useMemo(
    () =>
      // Filter out those answers that are pointing to a disabled flow templates
      filter(allAnswers, (answer) => {
        const isGoTo = answer.redirectType === 'goTo';
        const isGoToForm = answer.redirectType === 'goToForm';

        if (isGoTo) {
          const currentFlowTemplateItems = get(currentFlowTemplate, 'template.items');
          return !!find(currentFlowTemplateItems, { id: answer.properties.goTo });
        }

        if (isGoToForm) return !!find(flowTemplates, { _id: answer.properties.goToForm });

        return !!answer;
      }),
    [allAnswers, flowTemplates, currentFlowTemplate],
  );

  return (
    <ChatMessage message={message} {...rest}>
      {/* Display answers */}
      {message.metadata.isLastMessage && answers.length > 0 && (
        <div className={classnames(classes.choicesWrapper, { [classes.yesNoType]: isTypeYesNo })}>
          {map(answers, (answer, index) => {
            let icon;
            if (isTypeYesNo && index === 0) icon = <ThumbUpIcon fontSize="small" />;
            if (isTypeYesNo && index === 1) icon = <ThumbDownIcon fontSize="small" />;
            return (
              <Button
                data-lh-id={`button-${answer.id}`}
                key={answer.id}
                className={classnames(classes.button, { [classes.yesNoType]: isTypeYesNo })}
                variant="outlined"
                extraPadding
                extraRounded
                fullWidth
                icon={icon}
                onClick={() => {
                  onSubmit({
                    text: get(answer, 'label', ''),
                    type: 'outbound',
                    metadata: { ...message.metadata, answer },
                  });
                }}
              >
                <TypographyI18n variant="bW600F14" color="inherit" noWrap>
                  {getTranslation(answer, language, 'label')}
                </TypographyI18n>
              </Button>
            );
          })}
        </div>
      )}
    </ChatMessage>
  );
};

export default withStyles(styles, { name: 'MessageMultipleChoice' })(MessageMultipleChoice);
